import React from 'react';
import { DirectionsRun as RunIcon, DirectionsBike as RideIcon, Pool as SwimIcon, FitnessCenter as OtherIcon } from '@mui/icons-material';
import i18n from '../../i18n/i18n-setup';
import { intlLocale } from '../../i18n/locale-format';

export type ActivityKind = 'Run' | 'Ride' | 'Swim' | 'Other';

// Strava workout types vary ("Run", "TrailRun", "VirtualRide"...), so match loosely.
export const activityKindOf = (workoutType: string | undefined): ActivityKind => {
  const t = (workoutType || '').toLowerCase();
  if (t.includes('run')) return 'Run';
  if (t.includes('ride') || t.includes('bike') || t.includes('cycl')) return 'Ride';
  if (t.includes('swim')) return 'Swim';
  return 'Other';
};

// Display label for a kind in the active UI language.
export const activityKindLabel = (kind: ActivityKind): string => i18n.t(`activities:kind.${kind.toLowerCase()}`);

export const ActivityKindIcon = ({ kind }: { kind: ActivityKind }) => {
  switch (kind) {
    case 'Run': return <RunIcon fontSize="inherit" />;
    case 'Ride': return <RideIcon fontSize="inherit" />;
    case 'Swim': return <SwimIcon fontSize="inherit" />;
    default: return <OtherIcon fontSize="inherit" />;
  }
};

export const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0
    ? i18n.t('common:duration.hoursMinutes', { h, m: String(m).padStart(2, '0') })
    : i18n.t('common:duration.minutes', { m });
};

// Pace is decimal minutes per km; returns "5:40".
export const formatPace = (pace: number): string => {
  if (!isFinite(pace) || pace <= 0) return '–';
  let min = Math.floor(pace);
  let sec = Math.round((pace - min) * 60);
  if (sec === 60) { min += 1; sec = 0; }
  return `${min}:${String(sec).padStart(2, '0')}`;
};

export const speedKmh = (distanceKm: number, movingTimeSec: number): number =>
  movingTimeSec > 0 ? distanceKm / (movingTimeSec / 3600) : 0;

export const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const formatWhen = (date: Date | string): string =>
  new Date(date).toLocaleString(intlLocale(), { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export const sumDistance = (items: { distance: number }[]): number => items.reduce((s, a) => s + (a.distance || 0), 0);
export const sumTime = (items: { movingTime: number }[]): number => items.reduce((s, a) => s + (a.movingTime || 0), 0);
